import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Upload } from 'lucide-react';

const JobApplicationForm = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [jobDetails, setJobDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    candidateName: '',
    email: '',
    phone: '',
    yearsExperience: '',
    skills: '',
    currentCompany: '',
    currentJobTitle: '',
    currentLocation: '',
    expectedSalary: '',
    currentSalary: '',
    noticePeriod: '',
    preferredWorkMode: '',
    highestQualification: '',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // Show notification
  const showNotification = (message, type = 'info') => {
    const id = Date.now();
    const notification = { id, message, type };
    setNotifications(prev => [...prev, notification]);
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Fetch job details
  useEffect(() => {
    const fetchJobDetails = async () => {
      if (!jobId) return;
      
      try {
        setLoading(true);
        const response = await fetch('https://n8n.acespireconsulting.com/webhook/admin/jobs');
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const allJobs = await response.json();
        // Find the job with matching ID
        const job = allJobs.find(j => j.id === parseInt(jobId));
        
        if (!job) {
          throw new Error('Job not found');
        }
        
        setJobDetails(job);
      } catch (error) {
        console.error("Error fetching job details:", error);
        showNotification(`Error loading job details: ${error.message}`, 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchJobDetails();
  }, [jobId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type (PDF only)
    if (file.type !== 'application/pdf') {
      setErrors(prev => ({ ...prev, resume: 'Only PDF files are accepted' }));
      return;
    }
    
    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setErrors(prev => ({ ...prev, resume: 'File size should be less than 10MB' }));
      return;
    }
    
    setSelectedFile(file);
    // Clear error if file is valid
    if (errors.resume) {
      setErrors(prev => ({ ...prev, resume: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields validation
    if (!formData.candidateName.trim()) newErrors.candidateName = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.yearsExperience.trim()) newErrors.yearsExperience = 'Years of experience is required';
    if (!formData.skills.trim()) newErrors.skills = 'Skills are required';
    if (!formData.currentCompany.trim()) newErrors.currentCompany = 'Current company is required';
    if (!formData.currentJobTitle.trim()) newErrors.currentJobTitle = 'Current job title is required';
    if (!formData.currentLocation.trim()) newErrors.currentLocation = 'Current location is required';
    if (!formData.expectedSalary) newErrors.expectedSalary = 'Expected salary is required';
    if (!formData.currentSalary) newErrors.currentSalary = 'Current salary is required';
    if (!formData.noticePeriod.trim()) newErrors.noticePeriod = 'Notice period is required';
    if (!formData.preferredWorkMode) newErrors.preferredWorkMode = 'Preferred work mode is required';
    if (!formData.highestQualification.trim()) newErrors.highestQualification = 'Highest qualification is required';
    if (!selectedFile) newErrors.resume = 'Resume is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showNotification("Please fill in all required fields.", 'error');
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      
      // Append form fields with the correct names expected by the API
      formDataToSend.append('candidateName', formData.candidateName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('yearsExperience', formData.yearsExperience);
      formDataToSend.append('skills', formData.skills);
      formDataToSend.append('currentCompany', formData.currentCompany);
      formDataToSend.append('currentJobTitle', formData.currentJobTitle);
      formDataToSend.append('currentLocation', formData.currentLocation);
      formDataToSend.append('expectedSalary', formData.expectedSalary);
      formDataToSend.append('currentSalary', formData.currentSalary);
      formDataToSend.append('noticePeriod', formData.noticePeriod);
      formDataToSend.append('preferredWorkMode', formData.preferredWorkMode);
      formDataToSend.append('highestQualification', formData.highestQualification);
      
      // Append the resume file
      formDataToSend.append('resume', selectedFile);
      
      // Make the API call
      const response = await fetch(`https://n8n.acespireconsulting.com/webhook/1e34db93-da7c-40ff-bed2-524f6b76806e/jobs/${jobId}/apply`, {
        method: 'POST',
        body: formDataToSend,
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! Status: ${response.status}`);
      }
      
      const result = await response.json();
      
      // Show success message
      showNotification("Application submitted successfully!", 'success');
      
      // Reset form after successful submission
      setFormData({
        candidateName: '',
        email: '',
        phone: '',
        yearsExperience: '',
        skills: '',
        currentCompany: '',
        currentJobTitle: '',
        currentLocation: '',
        expectedSalary: '',
        currentSalary: '',
        noticePeriod: '',
        preferredWorkMode: '',
        highestQualification: '',
      });
      setSelectedFile(null);
      
      // Redirect to success page or job listings after a short delay
      setTimeout(() => {
        navigate('/success', { state: { jobTitle: jobDetails?.job_title } });
      }, 2000);
      
    } catch (error) {
      console.error("Error submitting application:", error);
      showNotification(`Failed to submit application: ${error.message}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
      </div>
    );
  }

  if (!jobDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white shadow-lg rounded-lg p-8 max-w-md">
          <h2 className="text-2xl font-bold text-red-500 mb-4">Job Not Found</h2>
          <p className="text-gray-600 mb-6">The job you're looking for doesn't exist or has been removed.</p>
          <button 
            onClick={() => navigate('/')}
            className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-md transition-colors"
          >
            Back to Job Listings
          </button>
        </div>
      </div>
    );
  }

  // Display the application form
  return (
    <div className="min-h-screen bg-gray-100 py-8">
      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="fixed top-4 right-4 z-50 space-y-2">
          {notifications.map(notification => (
            <div
              key={notification.id}
              className={`px-4 py-3 rounded-lg shadow-lg max-w-sm ${
                notification.type === 'error' 
                  ? 'bg-red-500 text-white' 
                  : notification.type === 'success'
                    ? 'bg-green-500 text-white'
                    : 'bg-blue-500 text-white'
              }`}
            >
              <div className="flex justify-between items-center">
                <span className="text-sm">{notification.message}</span>
                <button
                  onClick={() => removeNotification(notification.id)}
                  className="ml-2 text-white hover:text-gray-200"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4">
        {/* Job details header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            Apply for: {jobDetails.job_title}
          </h1>
          
          {jobDetails.full_job_description ? (
            <div className="text-gray-600 mb-4 whitespace-pre-line">
              {jobDetails.full_job_description}
            </div>
          ) : (
            <div className="mb-4">
              {jobDetails.experience_required && (
                <p className="text-gray-600 mb-2">
                  <span className="font-medium">Experience Required:</span> {jobDetails.experience_required}
                </p>
              )}
              
              {jobDetails.required_skills && jobDetails.required_skills.length > 0 && (
                <p className="text-gray-600 mb-2">
                  <span className="font-medium">Required Skills:</span> {jobDetails.required_skills.join(', ')}
                </p>
              )}
              
              {jobDetails.additional_requirements && (
                <p className="text-gray-600">
                  <span className="font-medium">Additional Requirements:</span> {jobDetails.additional_requirements}
                </p>
              )}
            </div>
          )}
        </div>
        
        {/* Application form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Personal Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 mb-6">
            {/* Personal Information Fields */}
            <div>
              <label htmlFor="candidateName" className="block text-sm font-medium text-gray-700 mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="candidateName"
                name="candidateName"
                value={formData.candidateName}
                onChange={handleChange}
                className={`w-full px-3 py-2 border ${errors.candidateName ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500`}
                disabled={isSubmitting}
              />
              {errors.candidateName && (
                <p className="mt-1 text-sm text-red-500">{errors.candidateName}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-3 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500`}
                disabled={isSubmitting}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-500">{errors.email}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`w-full px-3 py-2 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500`}
                disabled={isSubmitting}
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="yearsExperience" className="block text-sm font-medium text-gray-700 mb-1">
                Years of Experience <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="yearsExperience"
                name="yearsExperience"
                value={formData.yearsExperience}
                onChange={handleChange}
                className={`w-full px-3 py-2 border ${errors.yearsExperience ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500`}
                disabled={isSubmitting}
              />
              {errors.yearsExperience && (
                <p className="mt-1 text-sm text-red-500">{errors.yearsExperience}</p>
              )}
            </div>
            
            <div className="md:col-span-2">
              <label htmlFor="skills" className="block text-sm font-medium text-gray-700 mb-1">
                Skills <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="skills"
                name="skills"
                placeholder="e.g., JavaScript, React, Node.js"
                value={formData.skills}
                onChange={handleChange}
                className={`w-full px-3 py-2 border ${errors.skills ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500`}
                disabled={isSubmitting}
              />
              {errors.skills && (
                <p className="mt-1 text-sm text-red-500">{errors.skills}</p>
              )}
            </div>
          </div>
          
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Professional Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 mb-6">
            {/* Professional Information Fields */}
            <div>
              <label htmlFor="currentCompany" className="block text-sm font-medium text-gray-700 mb-1">
                Current Company <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="currentCompany"
                name="currentCompany"
                value={formData.currentCompany}
                onChange={handleChange}
                className={`w-full px-3 py-2 border ${errors.currentCompany ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500`}
                disabled={isSubmitting}
              />
              {errors.currentCompany && (
                <p className="mt-1 text-sm text-red-500">{errors.currentCompany}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="currentJobTitle" className="block text-sm font-medium text-gray-700 mb-1">
                Current Job Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="currentJobTitle"
                name="currentJobTitle"
                value={formData.currentJobTitle}
                onChange={handleChange}
                className={`w-full px-3 py-2 border ${errors.currentJobTitle ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500`}
                disabled={isSubmitting}
              />
              {errors.currentJobTitle && (
                <p className="mt-1 text-sm text-red-500">{errors.currentJobTitle}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="currentLocation" className="block text-sm font-medium text-gray-700 mb-1">
                Current Location <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="currentLocation"
                name="currentLocation"
                value={formData.currentLocation}
                onChange={handleChange}
                className={`w-full px-3 py-2 border ${errors.currentLocation ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500`}
                disabled={isSubmitting}
              />
              {errors.currentLocation && (
                <p className="mt-1 text-sm text-red-500">{errors.currentLocation}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="currentSalary" className="block text-sm font-medium text-gray-700 mb-1">
                Current Salary (LPA) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="currentSalary"
                name="currentSalary"
                value={formData.currentSalary}
                onChange={handleChange}
                className={`w-full px-3 py-2 border ${errors.currentSalary ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500`}
                disabled={isSubmitting}
              />
              {errors.currentSalary && (
                <p className="mt-1 text-sm text-red-500">{errors.currentSalary}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="expectedSalary" className="block text-sm font-medium text-gray-700 mb-1">
                Expected Salary (LPA) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="expectedSalary"
                name="expectedSalary"
                value={formData.expectedSalary}
                onChange={handleChange}
                className={`w-full px-3 py-2 border ${errors.expectedSalary ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500`}
                disabled={isSubmitting}
              />
              {errors.expectedSalary && (
                <p className="mt-1 text-sm text-red-500">{errors.expectedSalary}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="noticePeriod" className="block text-sm font-medium text-gray-700 mb-1">
                Notice Period <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="noticePeriod"
                name="noticePeriod"
                placeholder="e.g., 30 days"
                value={formData.noticePeriod}
                onChange={handleChange}
                className={`w-full px-3 py-2 border ${errors.noticePeriod ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500`}
                disabled={isSubmitting}
              />
              {errors.noticePeriod && (
                <p className="mt-1 text-sm text-red-500">{errors.noticePeriod}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="preferredWorkMode" className="block text-sm font-medium text-gray-700 mb-1">
                Preferred Work Mode <span className="text-red-500">*</span>
              </label>
              <select
                id="preferredWorkMode"
                name="preferredWorkMode"
                value={formData.preferredWorkMode}
                onChange={handleChange}
                className={`w-full px-3 py-2 border ${errors.preferredWorkMode ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500`}
                disabled={isSubmitting}
              >
                <option value="">Select work mode</option>
                <option value="Remote">Remote</option>
                <option value="Hybrid">Hybrid</option>
                <option value="Onsite">Onsite</option>
                <option value="Flexible">Flexible</option>
              </select>
              {errors.preferredWorkMode && (
                <p className="mt-1 text-sm text-red-500">{errors.preferredWorkMode}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="highestQualification" className="block text-sm font-medium text-gray-700 mb-1">
                Highest Qualification <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="highestQualification"
                name="highestQualification"
                placeholder="e.g., Bachelor's in Computer Science"
                value={formData.highestQualification}
                onChange={handleChange}
                className={`w-full px-3 py-2 border ${errors.highestQualification ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500`}
                disabled={isSubmitting}
              />
              {errors.highestQualification && (
                <p className="mt-1 text-sm text-red-500">{errors.highestQualification}</p>
              )}
            </div>
          </div>
          
          {/* Resume Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Resume (PDF only) <span className="text-red-500">*</span>
            </label>
            <div className={`border ${errors.resume ? 'border-red-500' : 'border-gray-300'} rounded-md p-4`}>
              <div className="flex items-center justify-between">
                <input
                  type="file"
                  id="resume"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={isSubmitting}
                />
                <label
                  htmlFor="resume"
                  className={`flex items-center cursor-pointer ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                >
                  <div className="bg-gray-100 rounded-md px-3 py-2 mr-3">
                    Choose File
                  </div>
                  <span className="text-gray-500 text-sm">
                    {selectedFile ? selectedFile.name : 'No file chosen'}
                  </span>
                </label>
                <Upload className="w-5 h-5 text-gray-400" />
              </div>
            </div>
            {errors.resume && (
              <p className="mt-1 text-sm text-red-500">{errors.resume}</p>
            )}
            {selectedFile && (
              <p className="mt-1 text-sm text-gray-500">
                File size: {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
              </p>
            )}
          </div>
          
          <div className="flex justify-center mt-8">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-6 py-3 bg-[#275F97] text-white font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-300 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobApplicationForm;